/**
 * The signed-in user's own account: the second factor, and what they are
 * willing to be told about.
 *
 * Neither endpoint takes a permission — they act on the caller and nobody
 * else. Both refuse an Idempotency-Key, because each request changes security
 * state and the confirmation response is not replayable.
 *
 * Two facts the screen is built to respect:
 *
 *   • The provisioning secret is shown once, in the response that mints it,
 *     and expires in fifteen minutes. It is never fetched again.
 *   • Confirming REVOKES every other session and returns exactly ten recovery
 *     codes that are never shown again.
 */
import type { components } from "@erp/api-client";

import { apiFetch } from "@/lib/api/http";

type Schemas = components["schemas"];

export type TOTPEnrollment = Schemas["TOTPEnrollment"];
export type TOTPConfirmationResponse = Schemas["TOTPConfirmationResponse"];
export type NotificationPreference = Schemas["NotificationPreference"];
export type NotificationChannel = Schemas["NotificationChannel"];

export const NOTIFICATION_CHANNELS: readonly NotificationChannel[] = ["in_app", "email"];

/** Re-authenticates with the current password and replaces any unconfirmed
 *  enrolment. The secret in the answer is the only copy the client ever sees. */
export function startTOTPEnrollment(password: string): Promise<TOTPEnrollment> {
  return apiFetch<TOTPEnrollment>("/totp-enrollment", {
    method: "POST",
    body: { password },
  });
}

/** Consumes one fresh code, enables the factor, revokes every prior session,
 *  and answers ten one-time recovery codes that are never shown again. */
export function confirmTOTPEnrollment(code: string): Promise<TOTPConfirmationResponse> {
  return apiFetch<TOTPConfirmationResponse>("/totp-enrollment/confirm", {
    method: "POST",
    body: { code },
  });
}

export function getNotificationPreferences(
  signal?: AbortSignal,
): Promise<{ items: readonly NotificationPreference[] }> {
  return apiFetch<{ items: readonly NotificationPreference[] }>("/notification-preferences", {
    signal,
  });
}

/** The supplied set BECOMES the stored set: anything omitted is deleted, which
 *  is how an opt-out is undone. */
export function replaceNotificationPreferences(
  items: readonly NotificationPreference[],
): Promise<{ items: readonly NotificationPreference[] }> {
  return apiFetch<{ items: readonly NotificationPreference[] }>("/notification-preferences", {
    method: "PUT",
    body: { items },
  });
}

/** Absence means enabled, and a template scope outranks a category scope. So a
 *  stored row is only ever an opt-out, and this answers what actually reaches
 *  the user for one template on one channel. */
export function deliversTo(
  preferences: readonly NotificationPreference[],
  templateCode: string,
  category: string,
  channel: NotificationChannel,
): boolean {
  const template = preferences.find(
    (preference) =>
      preference.scope_type === "template" &&
      preference.scope_value === templateCode &&
      preference.channel === channel,
  );
  if (template !== undefined) return template.enabled;
  const byCategory = preferences.find(
    (preference) =>
      preference.scope_type === "category" &&
      preference.scope_value === category &&
      preference.channel === channel,
  );
  return byCategory === undefined ? true : byCategory.enabled;
}
